import { FieldErrors } from 'react-hook-form';
import { get } from 'lodash';

import { AllowanceDiscrepancy, ChargeDiscrepancy, InvoiceDiscrepancy } from './types';

/**
 * Path parsing constants for different discrepancy types
 */
const DISCREPANCY_PATH_CONFIG = {
  CHARGES_MAX_DEPTH: 2, // charges paths have max 2 segments (e.g., "charges.0")
  FIELD_SEGMENTS_TO_REMOVE: 2, // Remove last 2 segments for field paths (field.value)
} as const;

/**
 * Extracts all field errors from a react-hook-form errors object
 * and formats them into InvoiceDiscrepancy objects.
 *
 * @param errors - The react-hook-form errors object
 * @param data - The form data used to extract related information
 * @returns A record of discrepancies keyed by their error path
 */
export const getAllInvoiceDiscrepancies = (
  errors: FieldErrors,
  data: Record<string, unknown>,
): Record<string, InvoiceDiscrepancy> => {
  const discrepancies: Record<string, InvoiceDiscrepancy> = {};

  traverseErrors(errors, '', data, discrepancies);

  return discrepancies;
};

/**
 * Determines the discrepancy type based on the path structure
 */
const getDiscrepancyType = (pathSegments: Array<string>): InvoiceDiscrepancy['type'] => {
  if (pathSegments.length <= DISCREPANCY_PATH_CONFIG.CHARGES_MAX_DEPTH) {
    // Short paths like "charges.0" indicate charge discrepancies
    return pathSegments[0] as InvoiceDiscrepancy['type'];
  }

  // For longer paths like "line_items.0.quantities.field.value",
  // the type is the segment before the field name
  return pathSegments.at(-3) as InvoiceDiscrepancy['type'];
};

/**
 * Extracts the data path from the error path, removing field-specific segments
 */
const extractDataPath = (pathSegments: Array<string>): string => {
  if (pathSegments.length <= DISCREPANCY_PATH_CONFIG.CHARGES_MAX_DEPTH) {
    // For charge paths, use the full path
    return pathSegments.join('.');
  }

  // For other paths, remove the last 2 segments (field.value)
  return pathSegments.slice(0, -DISCREPANCY_PATH_CONFIG.FIELD_SEGMENTS_TO_REMOVE).join('.');
};

/**
 * Creates a discrepancy object based on the path type and data
 */
const createDiscrepancy = (
  errorId: string,
  type: InvoiceDiscrepancy['type'],
  dataPath: string,
  formData: Record<string, unknown>,
): InvoiceDiscrepancy => {
  const extractedData = get(formData, dataPath);

  if (type === 'charges' || type === 'allowances') {
    // For charges, we need to return the actual charge data, not wrap it
    return {
      id: errorId,
      fieldPath: errorId,
      severity: 'error',
      type,
      data: extractedData,
      currency: get(formData, 'currency'),
    } as ChargeDiscrepancy | AllowanceDiscrepancy;
  }

  //matched_line_items.0.quantities.invoiced_quantity.value
  //matched_line_items.0.item.prices.consolidated_unit_price.actual
  // matched_line_items.0
  const lineItemPath = errorId.split('.').slice(0, 2).join('.');
  return {
    id: errorId,
    fieldPath: errorId,
    severity: 'error',
    type,
    data: extractedData,
    currency: get(formData, 'currency'),
    relatedTransactions: get(formData, `${lineItemPath}.related_messages`),
    lineItemPath,
  } as InvoiceDiscrepancy;
};

/**
 * Processes a message field error and converts it to a discrepancy
 */
const processMessageError = (errorPath: string, formData: Record<string, unknown>): InvoiceDiscrepancy => {
  // Remove '.message' suffix and split into path segments
  const cleanPath = errorPath.replace('.message', '');
  const pathSegments = cleanPath.split('.');

  const discrepancyType = getDiscrepancyType(pathSegments);
  const dataPath = extractDataPath(pathSegments);

  // Use cleanPath (without .message) as the ID
  return createDiscrepancy(cleanPath, discrepancyType, dataPath, formData);
};

/**
 * Recursively traverses the errors object to find message fields
 */
const traverseErrors = (
  errorObj: Record<string, unknown>,
  currentPath = '',
  formData: Record<string, unknown>,
  discrepancies: Record<string, InvoiceDiscrepancy>,
): void => {
  if (typeof errorObj !== 'object' || errorObj === null) {
    return;
  }

  for (const [key, value] of Object.entries(errorObj)) {
    // Skip react-hook-form internal fields
    if (key === 'ref') {
      continue;
    }

    const fullPath = currentPath ? `${currentPath}.${key}` : key;

    if (key === 'message') {
      // Found a message field - create a discrepancy
      const discrepancy = processMessageError(fullPath, formData);
      // Use the discrepancy ID (which is the clean path) as the key
      discrepancies[discrepancy.id] = discrepancy;
    } else if (typeof value === 'object' && value !== null) {
      // Continue traversing nested objects
      traverseErrors(value as Record<string, unknown>, fullPath, formData, discrepancies);
    }
  }
};
